"use client";

import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { PlanPrice } from "@/components/subscribe/PlanPrice";

export interface Plan {
  id: string;
  name: string;
  price: string;
  period: string;
  priceSublabel?: string;
  compareAtPrice?: string;
  promotionText?: string;
  description: string;
  features: string[];
  highlighted?: boolean;
}

interface PlanSelectorProps {
  plans: Plan[];
  selectedPlanId: string | null;
  onSelect: (id: string) => void;
}

export function PlanSelector({
  plans,
  selectedPlanId,
  onSelect,
}: PlanSelectorProps) {
  const tp = useTranslations("Plans");

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {plans.map((plan) => {
        const isSelected = selectedPlanId === plan.id;
        return (
          <button
            key={plan.id}
            type="button"
            onClick={() => onSelect(plan.id)}
            className={`text-left p-6 rounded-xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
              isSelected
                ? "border-teal-primary bg-teal-primary/10"
                : "border-slate-700 hover:border-slate-600 bg-slate-800/30"
            } ${plan.highlighted ? "ring-2 ring-teal-primary/30" : ""}`}
          >
            {plan.highlighted && (
              <span className="inline-block px-3 py-0.5 text-xs font-semibold text-teal-light bg-teal-primary/20 rounded-full mb-4">
                {tp("popular")}
              </span>
            )}
            <h3 className="text-lg font-semibold text-slate-100 mb-1">
              {plan.name}
            </h3>
            <PlanPrice
              price={plan.price}
              period={plan.period}
              compareAtPrice={plan.compareAtPrice}
              promotionText={plan.promotionText}
            />
            {plan.priceSublabel ? (
              <p className="text-xs text-teal-light/80 mb-2">{plan.priceSublabel}</p>
            ) : null}
            <p className="text-slate-400 text-sm mb-4">{plan.description}</p>
            <ul className="space-y-2 mb-4">
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-slate-300 text-sm">
                  <Check className="w-4 h-4 text-electric-blue shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                isSelected ? "border-teal-primary bg-teal-primary" : "border-slate-600"
              }`}
            >
              {isSelected && <Check className="w-3 h-3 text-white" />}
            </div>
          </button>
        );
      })}
    </div>
  );
}
